import React from 'react';
import { Link } from 'react-router-dom';
import Navbar from '../components/Navbar';
import SEO from '../components/SEO';
import './ReportViewer.css';

const FreeReportViewer = () => {
  // SEO Configuration
  const pageTitle = "View Free Impact Report | The Prisma Global";
  const pageDescription = "View The Prisma Global's free impact report summary. See how we've empowered 280+ women, secured ₹2.62Cr funding, and built 4 operational divisions from Solapur. Download PDF or upgrade to premium detailed report (₹999).";
  const pageKeywords = "free impact report viewer, business report online, Prisma Global report, Solapur impact data, free business report download, view impact report";
  
  const structuredData = {
    "@context": "https://schema.org",
    "@type": "WebPage",
    "name": "Free Impact Report",
    "description": "Free summary impact report of The Prisma Global",
    "url": "https://theprismaglobal.com/reports/free",
    "potentialAction": {
      "@type": "DownloadAction",
      "target": "https://theprismaglobal.com/reports/free"
    }
  };
  
  const handleDownloadPDF = () => {
    // Hide navbar for PDF
    const navbar = document.querySelector('.site-header');
    if (navbar) navbar.style.display = 'none';
    
    // Trigger print dialog
    window.print();
    
    // Restore navbar after print dialog closes
    setTimeout(() => {
      if (navbar) navbar.style.display = 'block';
    }, 100);
  };

  return (
    <div className="report-viewer">
      <SEO 
        title={pageTitle}
        description={pageDescription}
        keywords={pageKeywords}
        canonical="/reports/free"
        ogImage="https://theprismaglobal.com/hero-main.png"
        structuredData={structuredData}
      />
      
      {/* Navigation - Same as main site */}
      <Navbar />

      {/* Report Content */}
      <div className="report-container">
        {/* Page 1: Cover */}
        <div className="report-page cover-page">
          <div className="cover-header">
            <div className="cover-badge">Impact Report 2025 • Summary Edition</div>
            <h1 className="cover-title">
              THE PRISMA<br/>
              <span className="highlight">GLOBAL</span>
            </h1>
            <p className="cover-subtitle">
              Building purpose-driven enterprises from Tier-2 India. 
              Where local culture, innovation, and global ambition converge.
            </p>
          </div>
          
          <div className="cover-footer">
            <div className="cover-stats">
              <div className="cover-stat">
                <div className="cover-stat-value">300K+</div>
                <div className="cover-stat-label">Users Reached</div>
              </div>
              <div className="cover-stat">
                <div className="cover-stat-value">$230K+</div>
                <div className="cover-stat-label">Revenue & Grants</div>
              </div>
              <div className="cover-stat">
                <div className="cover-stat-value">₹2.62Cr</div>
                <div className="cover-stat-label">Govt Funding</div>
              </div>
              <div className="cover-stat">
                <div className="cover-stat-value">280+</div>
                <div className="cover-stat-label">Women Jobs Planned</div>
              </div>
            </div>
            
            <div className="cover-info">
              <div className="cover-company">THE PRISMA GLOBAL</div>
              <div className="cover-date">Solapur, Maharashtra • 2025</div>
            </div>
          </div>
        </div>

        {/* Page 2: Executive Overview */}
        <div className="report-page">
          <div className="page-header">
            <div className="page-header-logo">THE PRISMA GLOBAL</div>
            <div className="page-header-title">Impact Report 2025</div>
          </div>
          
          <h1 className="section-title">Executive Overview</h1>
          
          <p className="report-text">The Prisma Global is a purpose-driven enterprise headquartered in Solapur, Maharashtra, founded in 2025 by Ms. Veda Lolge. Building on over a decade of entrepreneurial experience across India and Mauritius, the Group consolidates proven ventures into a multi-vertical structure focused on digital platforms, textile manufacturing, real estate, and strategic advisory.</p>
          
          <div className="highlight-box">
            <p>"The Prisma Global exists to empower local communities by building purpose-driven physical and digital systems that transform regional culture, education, and craftsmanship into globally scalable value—anchored in Solapur and designed for the world."</p>
          </div>
          
          <h2 className="subsection-title">Key Impact Metrics</h2>
          
          <div className="stats-grid">
            <div className="stat-box">
              <div className="value">300K+</div>
              <div className="label">Users Reached</div>
            </div>
            <div className="stat-box">
              <div className="value">$230K+</div>
              <div className="label">Revenue & Grants</div>
            </div>
            <div className="stat-box">
              <div className="value">₹12Cr+</div>
              <div className="label">Asset Base</div>
            </div>
            <div className="stat-box">
              <div className="value">8+</div>
              <div className="label">Awards Won</div>
            </div>
          </div>
          
          <h2 className="subsection-title">Vision Statement</h2>
          <p className="report-text">Prisma Global envisions a future where Solapur becomes a globally connected, locally empowered economic hub—driven by circular systems, rural employment, cultural integrity, and innovation.</p>
        </div>

        {/* Page 3: Business Divisions */}
        <div className="report-page">
          <div className="page-header">
            <div className="page-header-logo">THE PRISMA GLOBAL</div>
            <div className="page-header-title">Impact Report 2025</div>
          </div>
          
          <h1 className="section-title">Business Divisions</h1>
          
          <div className="division-grid">
            <div className="division-card">
              <h4>🖥️ Prisma Digital</h4>
              <p>Digital platforms, media systems, and technology-enabled products for cultural discovery, education, and brand-led community engagement. Home to Discover Solapur™ and Eco-Warriors™.</p>
            </div>
            <div className="division-card">
              <h4>🧵 PrismaTex</h4>
              <p>Textile and apparel vertical rooted in Solapur's weaving heritage. Combines traditional craftsmanship with contemporary design, planning to employ 280+ women in manufacturing.</p>
            </div>
            <div className="division-card">
              <h4>🏗️ Prisma Terra</h4>
              <p>Land aggregation, plotting, and real estate advisory services. Flagship project: Nakshatra Villas with approximately ₹10 Crore in land assets.</p>
            </div>
            <div className="division-card">
              <h4>📋 Prisma Policy</h4>
              <p>Strategic advisory and campaign management for public leaders and institutions. Notable: 2024 Maharashtra election campaign success.</p>
            </div>
          </div>
          
          <h2 className="subsection-title">Key Milestones</h2>
          
          <div className="timeline">
            <div className="timeline-item">
              <div className="timeline-year">2014</div>
              <div className="timeline-text">Founded Panda & Wolf Advertising Agency in Pune</div>
            </div>
            <div className="timeline-item">
              <div className="timeline-year">2018</div>
              <div className="timeline-text">Launched Discover Mauritius™ cultural travel guide app</div>
            </div>
            <div className="timeline-item">
              <div className="timeline-year">2019</div>
              <div className="timeline-text">Eco-Warriors™ launched with UNESCO partnership; Won Seedstars</div>
            </div>
            <div className="timeline-item">
              <div className="timeline-year">2020</div>
              <div className="timeline-text">Received JICA Grant (USD 30,000) and MRIC Research Grant</div>
            </div>
            <div className="timeline-item">
              <div className="timeline-year">2021</div>
              <div className="timeline-text">300K users on Discover Mauritius™; UNESCO Patronage</div>
            </div>
            <div className="timeline-item">
              <div className="timeline-year">2024</div>
              <div className="timeline-text">₹2.62Cr NCDC funding; Winning election campaign</div>
            </div>
            <div className="timeline-item">
              <div className="timeline-year">2025</div>
              <div className="timeline-text">Founded The Prisma Global</div>
            </div>
          </div>
        </div>

        {/* Page 4: Founder */}
        <div className="report-page">
          <div className="page-header">
            <div className="page-header-logo">THE PRISMA GLOBAL</div>
            <div className="page-header-title">Impact Report 2025</div>
          </div>
          
          <h1 className="section-title">Founder Profile</h1>
          
          <div className="founder-section">
            <div className="founder-image">
              <img src="/founder-headshot-new.png" alt="Veda Lolge" />
            </div>
            <div className="founder-info">
              <h3>Ms. Veda Lolge</h3>
              <p><strong>CEO & Founder</strong></p>
              <p>B.F.A (Fine & Applied Arts) with 10+ years of entrepreneurial experience across India and Mauritius.</p>
            </div>
          </div>
          
          <div className="highlight-box">
            <p>"I built Prisma Global because I have seen firsthand how much potential exists in local communities—and how often that potential is overlooked. This company was created to ensure that knowledge, culture, and skilled work from places like Solapur are valued, protected, and scaled with dignity."</p>
          </div>
          
          <h2 className="subsection-title">Awards & Recognition</h2>
          
          <div className="awards-list">
            <div className="award-item">
              <div className="award-year">2022</div>
              <div className="award-text">Africa's Women Leader — World Women Leadership Congress</div>
            </div>
            <div className="award-item">
              <div className="award-year">2021</div>
              <div className="award-text">UNESCO Patronage for Eco-Warriors™</div>
            </div>
            <div className="award-item">
              <div className="award-year">2020</div>
              <div className="award-text">NINJA Competition Winner (USD 30,000) — JICA</div>
            </div>
            <div className="award-item">
              <div className="award-year">2020</div>
              <div className="award-text">Best Sustainable EdTech Award — Women in Tech Africa</div>
            </div>
            <div className="award-item">
              <div className="award-year">2019</div>
              <div className="award-text">Female Role Model of the Year — Global Startup Awards</div>
            </div>
          </div>
        </div>

        {/* Page 5: Premium Teaser & Contact */}
        <div className="report-page">
          <div className="page-header">
            <div className="page-header-logo">THE PRISMA GLOBAL</div>
            <div className="page-header-title">Impact Report 2025</div>
          </div>
          
          <h1 className="section-title">Want the Complete Picture?</h1>
          
          <div className="premium-teaser">
            <h3>📊 Premium Detailed Report — ₹999</h3>
            <p>Get the full 16-page investor-ready report with:</p>
            <ul>
              <li>Detailed Financial Analysis & Growth Metrics</li>
              <li>Complete Division-by-Division Breakdown</li>
              <li>Case Studies: Eco-Warriors, Election Campaign</li>
              <li>Investment Thesis & Asset Valuation</li>
              <li>Full Partner & Client Portfolio</li>
            </ul>
            <Link to="/impact-report#premium-purchase" className="cta-button">Get Premium Report — ₹999 →</Link>
          </div>
          
          <h2 className="subsection-title">Connect With Us</h2>
          
          <div className="contact-grid">
            <div className="contact-item">
              <div className="contact-label">Email</div>
              <div className="contact-value">info@theprismaglobal.com</div>
            </div>
            <div className="contact-item">
              <div className="contact-label">Phone</div>
              <div className="contact-value">+91 9356 107 460</div>
            </div>
            <div className="contact-item">
              <div className="contact-label">Website</div>
              <div className="contact-value">www.theprismaglobal.com</div>
            </div>
            <div className="contact-item">
              <div className="contact-label">Location</div>
              <div className="contact-value">Solapur, Maharashtra, India</div>
            </div>
          </div>
          
          <div className="report-footer-text">
            <p><strong>THE PRISMA GLOBAL</strong></p>
            <p>Where Light Becomes Legacy</p>
            <p>© 2025 The Prisma Global. All Rights Reserved.</p>
          </div>
        </div>
      </div>

      {/* Download Button */}
      <div className="download-section">
        <button onClick={handleDownloadPDF} className="download-btn">
          📥 Download as PDF
        </button>
        <p className="download-hint">Click to download the report as PDF</p>
      </div>
    </div>
  );
};

export default FreeReportViewer;
